import React, { useEffect, useState } from 'react';
import { AssessmentVersion, getRiskSourcesName, getRiskEventsName, getRiskConsequencesName } from 'src/api/types';
import { ComparisonAssessmentQuery } from 'src/graphql/_generated';
import styled from 'styled-components';
import _, { orderBy } from 'lodash';
import { ModifiedScenario, ModifiedScenarioNamed, ScenariosComparison } from '../util';
import { Poppins, Spacer } from 'src/common';
import { HeadRow, Row } from './comps';

const Div = styled.div`
  position: relative;
  overflow-y: hidden;

  .grid {
    display: grid;
    grid-template-columns: minmax(200px, 600px) minmax(470px, 1500px) minmax(470px, 1500px);
    grid-gap: 8px;

    &__item {
      border-top-right-radius: 6px;
      border-top-left-radius: 6px;
      padding: 9px 18px;
      background-color: ${({ theme }) => theme.colors.bgGray};
      border: 1px solid ${({ theme }) => theme.colors.stroke};

      &__head {
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        grid-gap: 8px;

        &__title {
          line-height: normal;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        &__version {
          margin-top: 3px;
        }

        &__scenarios {
          line-height: 14px;
          display: flex;
          /* align-items: flex-end; */
          grid-gap: 8px;
          height: 10px;
          &__label {
            margin-top: 2px;
          }
        }
      }
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .grid {
      grid-template-columns: minmax(150px, 600px) minmax(370px, 1500px) minmax(370px, 1500px);
    }
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    .grid {
      grid-template-columns: minmax(120px, 600px) minmax(260px, 1500px) minmax(260px, 1500px);
      .scenarios-ammount {
        margin-top: 5px;
      }
      .label {
        margin-left: 4px;
        margin-bottom: 1px;
      }
    }
  }
`;

type AssessmentData = NonNullable<ComparisonAssessmentQuery['assessmentA']>;

interface ComparisonViewProps {
  dataA: AssessmentData;
  dataB: AssessmentData;
  versionA?: AssessmentVersion;
  versionB?: AssessmentVersion;
  comparison: ScenariosComparison | null;
  onScenarioClick?: (scenario: AssessmentData['scenarios'][0], from: 'A' | 'B') => void;
  genPath: (a: AssessmentData['scenarios'][0], from: 'A' | 'B') => string | undefined;
}

interface ComparedScenario {
  rowA: ModifiedScenario | undefined;
  rowB: ModifiedScenario | undefined;
  dataChanges?: boolean;
}
interface ComparedScenarioNamed {
  rowA: ModifiedScenarioNamed | undefined;
  rowB: ModifiedScenarioNamed | undefined;
  dataChanges?: boolean;
}

const getComparedScenarios = (comparison: ScenariosComparison | null): ComparedScenarioNamed[] => {
  const temp: ComparedScenario[] = [];

  comparison?.compareA.exactMatch.forEach((el, idx) => {
    temp.push({ rowA: el, rowB: comparison.compareB.exactMatch[idx] });
  });
  comparison?.compareA.dataChanges.forEach((el, idx) => {
    temp.push({ rowA: el, rowB: comparison.compareB.dataChanges[idx], dataChanges: true });
  });
  comparison?.compareA.unique.forEach((el) => {
    temp.push({ rowA: el, rowB: undefined });
  });
  comparison?.compareB.unique.forEach((el) => {
    temp.push({ rowA: undefined, rowB: el });
  });

  return temp.map((el) => {
    return {
      ...el,
      rowA: el.rowA
        ? {
            ...el.rowA,
            source: {
              ...el.rowA.source,
              value: getRiskSourcesName(el.rowA.source?.value),
              hasChanges: el.rowA.source?.hasChanges || false,
            },
            consequence: {
              ...el.rowA.consequence,
              value: getRiskConsequencesName(el.rowA.consequence?.value),
              hasChanges: el.rowA.consequence?.hasChanges || false,
            },
            event: {
              ...el.rowA.event,
              value: getRiskEventsName(el.rowA.event?.value),
              hasChanges: el.rowA.event?.hasChanges || false,
            },
          }
        : undefined,
      rowB: el.rowB
        ? {
            ...el.rowB,
            source: {
              ...el.rowB.source,
              value: getRiskSourcesName(el.rowB.source?.value),
              hasChanges: el.rowB.source?.hasChanges || false,
            },
            consequence: {
              ...el.rowB.consequence,
              value: getRiskConsequencesName(el.rowB.consequence?.value),
              hasChanges: el.rowB.consequence?.hasChanges || false,
            },
            event: {
              ...el.rowB.event,
              value: getRiskEventsName(el.rowB.event?.value),
              hasChanges: el.rowB.event?.hasChanges || false,
            },
          }
        : undefined,
    };
  });
};

const ComparisonView: React.FC<ComparisonViewProps> = ({
  comparison,
  dataA,
  dataB,
  onScenarioClick,
  versionA,
  versionB,
  genPath,
}) => {
  const [comparedScenarios, setComparedScenarios] = useState<ComparedScenarioNamed[]>([]);
  const [sort, setSort] = useState<{ isAsc: boolean; selectedSort: string; type: 'A' | 'B' }>({
    isAsc: false,
    selectedSort: '',
    type: 'A',
  });

  const genPathInner = (referenceId: string, from: 'A' | 'B') => {
    const scen =
      from === 'A'
        ? dataA.scenarios.find((el) => el.referenceId === referenceId)
        : dataB.scenarios.find((el) => el.referenceId === referenceId);

    return scen ? genPath(scen, from) || '' : '';
  };

  useEffect(() => {
    const temp = getComparedScenarios(comparison);
    let sorted: ComparedScenarioNamed[] = [];

    if (sort.selectedSort === 'name') {
      sorted = _.orderBy(
        temp,
        [
          (item) => {
            const row = (item.rowA || item.rowB) as ModifiedScenarioNamed;
            return row.name?.value?.toLowerCase() || '';
          },
        ],
        [sort.isAsc ? 'asc' : 'desc'],
      );
    } else {
      sorted = _.orderBy(
        temp,
        [
          (item) => {
            const value = _.get(item, `row${sort.type}.${sort.selectedSort}`);
            return value === undefined;
          },
          (item) => {
            const value = _.get(item, `row${sort.type}.${sort.selectedSort}`);
            return value;
          },
        ],
        ['asc', sort.isAsc ? 'asc' : 'desc'],
      );
    }

    setComparedScenarios(sorted);
  }, [comparison, sort]);

  useEffect(() => {
    if (!sort.selectedSort && comparison) {
      const temp = getComparedScenarios(comparison);

      const sorted = orderBy(temp, [
        (item) => {
          const row = (item.rowA || item.rowB)!;
          return row.meta?.value?.createdAt || '';
        },
      ]);
      setComparedScenarios(sorted);
    }
  }, [comparison, sort.selectedSort]);

  const isSameAssessment = dataA.id === dataB.id;
  const hasVersion = Boolean(versionA || versionB);
  const showVersionLabels = isSameAssessment && hasVersion;
  const showVersionInfo = hasVersion;

  if (!comparison) return null;

  return (
    <Div className="styled-scroll">
      <div className="grid">
        <div className="grid__item" />
        <div className="grid__item">
          <div className="grid__item__head">
            <Poppins color="prussianBlue" className="grid__item__head__title" weight={600} px={24}>
              {showVersionLabels ? 'Version' : 'Assessment'} 1
            </Poppins>
            <div className="grid__item__head__version"></div>
            <div className="grid__item__head__scenarios">
              <Poppins color="prussianBlue" weight={600} px={24}>
                {dataA.scenarios.length}
              </Poppins>
              <Poppins color="cflowerBlue" className="grid__item__head__scenarios__label" px={14}>
                scenario{dataA.scenarios.length > 1 ? 's' : ''}
              </Poppins>
            </div>
          </div>
          <Spacer $px={4} />
          <Poppins px={14} weight={500} color="cflowerBlue">
            {showVersionInfo ? versionA?.name || '[CURRENT]' : '[CURRENT]'}
          </Poppins>
        </div>
        <div className="grid__item">
          <div className="grid__item__head">
            <Poppins color="prussianBlue" weight={600} px={24}>
              {showVersionLabels ? 'Version' : 'Assessment'} 2
            </Poppins>
            <div className="grid__item__head__version"></div>
            <div className="grid__item__head__scenarios">
              <Poppins color="prussianBlue" weight={600} px={24}>
                {dataB.scenarios.length}
              </Poppins>
              <Poppins color="cflowerBlue" className="grid__item__head__scenarios__label" px={14}>
                scenario{dataB.scenarios.length > 1 ? 's' : ''}
              </Poppins>
            </div>
          </div>
          <Spacer $px={4} />
          <Poppins px={14} weight={500} color="cflowerBlue">
            {showVersionInfo ? versionB?.name || '[CURRENT]' : '[CURRENT]'}
          </Poppins>
        </div>
      </div>
      <div style={{ position: 'relative' }}>
        <HeadRow sort={sort} onSortSelect={setSort} />
        {comparedScenarios.map((el, idx) => (
          <Row
            key={el.rowA?.id.value || '' + el.rowB?.id.value}
            dataA={el.rowA}
            dataB={el.rowB}
            noBorderTop={idx === 0}
            dataChanges={el.dataChanges}
            genPathInner={genPathInner}
          />
        ))}
      </div>
    </Div>
  );
};

export default ComparisonView;
